import { createFileRoute, Link } from "@tanstack/react-router";
import { Car, DoorOpen, KeyRound, Lock, Radio, Wrench } from "lucide-react";

import { JsonLd } from "@/components/site/JsonLd";
import { CtaBand, FaqSection, LocationCard, PageCard, TrustBar } from "@/components/site/Sections";
import { SiteLayout } from "@/components/site/SiteLayout";
import { WhatsAppButton } from "@/components/site/WhatsAppChat";
import { servicePages } from "@/data/pages";
import { business } from "@/lib/business";
import { breadcrumbSchema, canonicalLink, faqSchema, metaFor } from "@/lib/seo";

const title = "Chaveiro em Betim 24 Horas | Chavel Chaveiro desde 1975";
const description =
  "Chaveiro em Betim 24 horas: abertura de porta e de carro, chave codificada, cópia de chaves, fechadura digital e controle de portão. Chavel, na Av. Amazonas, Brasiléia.";

const faq = [
  {
    q: "Qual o melhor chaveiro em Betim?",
    a: `A Chavel atua em Betim desde ${business.since}, com loja física na Av. Amazonas, 1354 - Lj 01, bairro Brasiléia, nota ${business.rating} no Google com ${business.reviewCount} avaliações e atendimento 24 horas em emergências.`,
  },
  {
    q: "A Chavel atende 24 horas em Betim?",
    a: "Sim. A loja funciona de segunda a sexta das 08h às 18h e sábado das 08h às 13h, e o plantão de emergência atende 24 horas por telefone e WhatsApp (31) 3531-3544, inclusive em finais de semana e feriados.",
  },
  {
    q: "Quanto tempo demora para o chaveiro chegar?",
    a: "Na região central de Betim e bairros próximos como Brasiléia, Alterosa, Angola, Guarujá, Ingá e Dom Bosco, o atendimento a domicílio costuma chegar entre 20 e 40 minutos, conforme trânsito e demanda.",
  },
  {
    q: "A Chavel faz chave codificada de carro?",
    a: "Sim. Fazemos cópia e codificação de chaves com transponder, chave canivete e telecomando para as principais montadoras, com equipamento de programação próprio.",
  },
  {
    q: "Vocês vão até o local do cliente?",
    a: "Sim. Atendemos a domicílio em residências, condomínios, comércios e na rua em casos de porta batida, chave quebrada na fechadura ou carro trancado.",
  },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: metaFor({ title, description, path: "/" }),
    links: canonicalLink("/"),
  }),
  component: Home,
});

const servicos = [
  { icon: DoorOpen, title: "Abertura de portas", text: "Porta batida, chave perdida ou quebrada na fechadura, sem danificar a porta.", slug: "chaveiro-24-horas-betim" },
  { icon: Car, title: "Chaveiro automotivo", text: "Abertura de veículos, chave codificada, telecomando e chave canivete.", slug: "chaveiro-automotivo-betim" },
  { icon: KeyRound, title: "Cópia de chaves", text: "Chaves simples, tetra, gorje, codificadas e de segurança na hora.", slug: "chave-codificada" },
  { icon: Lock, title: "Fechaduras", text: "Troca, instalação e conserto de fechaduras de porta de madeira, blindex e alumínio.", slug: "fechaduras-de-porta" },
  { icon: Wrench, title: "Fechadura digital", text: "Venda e instalação de fechaduras digitais, biométricas e eletrônicas.", slug: "fechadura-digital" },
  { icon: Radio, title: "Controles de portão", text: "Cópia e programação de controles, incluindo Rossi, PPA, Garen e Peccinin.", slug: "controle-de-portao" },
];

function Home() {
  const emergencia = servicePages.filter((page) => page.category === "emergencia").slice(0, 6);
  const automotivo = servicePages.filter((page) => page.category === "automotivo");
  const bairros = servicePages.filter((page) => page.category === "bairro");

  return (
    <SiteLayout>
      <JsonLd data={faqSchema(faq)} />
      <JsonLd data={breadcrumbSchema([{ name: "Início", url: "/" }])} />

      <section className="bg-surface text-surface-foreground">
        <div className="container-page grid gap-10 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
          <div>
            <p className="inline-flex rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gold">
              Chaveiro em Betim desde {business.since}
            </p>
            <h1 className="mt-4 font-display text-4xl leading-tight md:text-5xl">
              Chaveiro em Betim 24 horas: abertura de portas, chaves codificadas e fechaduras
            </h1>
            <p className="mt-5 max-w-2xl text-base opacity-90">
              A Chavel é uma chavearia tradicional de Betim, com loja na Av. Amazonas, 1354, bairro Brasiléia. Atendemos
              residências, comércios, condomínios e veículos em toda a cidade — do Centro ao Alterosa, Angola, Guarujá,
              Ingá e Dom Bosco — com plantão de emergência 24 horas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <WhatsAppButton variant="gold">Pedir atendimento agora</WhatsAppButton>
              <a
                href={`tel:${business.phoneRaw}`}
                className="inline-flex items-center rounded-full border border-white/30 px-6 py-3 text-sm font-semibold hover:bg-white/10"
              >
                Ligar {business.phone}
              </a>
            </div>
            <p className="mt-4 text-sm opacity-75">
              Nota {business.rating} no Google • {business.reviewCount} avaliações • Atendimento a domicílio em Betim e região
            </p>
          </div>
          <LocationCard />
        </div>
      </section>

      <TrustBar />

      <section className="container-page py-14">
        <h2 className="font-display text-3xl">Serviços de chaveiro em Betim</h2>
        <p className="mt-2 max-w-3xl text-muted-foreground">
          Todo o serviço de chaveiro em um só lugar: emergência, automotivo, fechaduras e automação de portões. Orçamento
          informado antes da execução, sem surpresa no final.
        </p>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {servicos.map((servico) => (
            <Link
              key={servico.title}
              to="/$slug"
              params={{ slug: servico.slug }}
              className="group rounded-xl border border-border bg-card p-6 transition-shadow hover:shadow-[var(--shadow-soft)]"
            >
              <servico.icon className="h-7 w-7 text-gold" />
              <h3 className="mt-4 text-lg font-semibold group-hover:text-primary">{servico.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{servico.text}</p>
              <span className="mt-4 inline-block text-sm font-semibold text-primary">Ver detalhes →</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-muted/40 py-14">
        <div className="container-page">
          <h2 className="font-display text-3xl">Emergência e atendimento 24 horas</h2>
          <p className="mt-2 max-w-3xl text-muted-foreground">
            Ficou trancado para fora de casa ou do carro? Veja as páginas com o passo a passo de cada situação.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {emergencia.map((page) => (
              <PageCard key={page.slug} page={page} />
            ))}
          </div>
        </div>
      </section>

      <section className="container-page py-14">
        <h2 className="font-display text-3xl">Chaveiro automotivo e chave codificada</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {automotivo.map((page) => (
            <PageCard key={page.slug} page={page} />
          ))}
        </div>
      </section>

      <section className="bg-muted/40 py-14">
        <div className="container-page">
          <h2 className="font-display text-3xl">Chaveiro nos bairros de Betim</h2>
          <p className="mt-2 max-w-3xl text-muted-foreground">
            Atendimento a domicílio nos principais bairros da cidade, com deslocamento rápido a partir da Brasiléia.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {bairros.map((page) => (
              <PageCard key={page.slug} page={page} />
            ))}
          </div>
          <Link to="/servicos" className="mt-8 inline-block font-semibold text-primary underline">
            Ver todos os serviços e regiões atendidas
          </Link>
        </div>
      </section>

      <FaqSection items={faq} title="Perguntas frequentes sobre chaveiro em Betim" />

      <CtaBand
        title="Precisa de um chaveiro em Betim agora?"
        text="Conte o que aconteceu e onde você está. Em poucos minutos um técnico da Chavel está a caminho."
      />
    </SiteLayout>
  );
}
